import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux";
import { getAllServiceAreas, createServiceArea } from "../../../../redux/Actions/ServiceAreaAction";
import { getAllCountries, createCountry } from "../../../../redux/Actions/AdminAction";
import { Search, X, MapPin, AlertCircle } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { ServiceArea } from "../../../admin/models/ServiceArea.model";
import { Button } from "../../../../components/ui/Button";
import { GlobalService } from "../../../shared/services/global.service";
import {
    extractLocationFromPlace,
    findOrCreateCountry,
    generateMockPostalCode,
    LocationData
} from "../../../../lib/locationUtils";
import { RoleEnum } from "../../../shared";
import { useTranslation } from "react-i18next";

// Google Maps API TypeScript declarations
declare global {
    interface Window {
        google: any;
    }
}

interface ServiceAreaSelectionProps {
    selectedServiceAreas: ServiceArea[];
    professionalType?: any;
    onServiceAreasChange: (serviceAreas: ServiceArea[]) => void;
    disabled?: boolean;
}

const ServiceAreaSelection: React.FC<ServiceAreaSelectionProps> = ({
    selectedServiceAreas,
    professionalType,
    onServiceAreasChange,
    disabled = false
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [allServiceAreas, setAllServiceAreas] = useState<ServiceArea[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const { user } = useSelector((state: any) => state.auth);
    // Google Maps autocomplete states
    const [isGoogleMapsAvailable, setIsGoogleMapsAvailable] = useState(false);
    const [autocompletePlace, setAutocompletePlace] = useState<any>(null);
    const [showAutocompleteSuggestions, setShowAutocompleteSuggestions] = useState(false);
    const [isCreatingServiceArea, setIsCreatingServiceArea] = useState(false);
    // Refs
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get countries from Redux
    const { countries } = useSelector((state: any) => state.admin);

    useEffect(() => {
        fetchServiceAreas();
        initializeGoogleMaps();
    }, []);

    // Fetch countries for service area creation
    useEffect(() => {
        if (!countries || countries.length === 0) {
            dispatch(getAllCountries({ isShowLoader: false }));
        }
    }, [countries, dispatch]);

    // Initialize Google Maps
    const initializeGoogleMaps = () => {
        if (!window.google || !window.google.maps) {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!apiKey) {
                console.warn("Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.");
                return;
            }

            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log("Google Maps script loaded successfully");
                setIsGoogleMapsAvailable(true);
                initServiceAreaAutocomplete();
            };
            script.onerror = () => {
                console.error("Failed to load Google Maps script");
                setIsGoogleMapsAvailable(false);
            };
            document.head.appendChild(script);
        } else {
            console.log("Google Maps already available");
            setIsGoogleMapsAvailable(true);
            initServiceAreaAutocomplete();
        }
    };

    // Initialize Google Maps Autocomplete for service areas
    const initServiceAreaAutocomplete = () => {
        console.log("Initializing service area autocomplete...");
        console.log("Google Maps available:", !!window.google);
        console.log("Search input ref:", !!searchInputRef.current);

        if (!window.google || !window.google.maps || !searchInputRef.current) {
            console.log("Service area autocomplete initialization failed - missing dependencies");
            return;
        }

        console.log("Creating service area autocomplete instance...");
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            types: ["geocode", "establishment"],
            componentRestrictions: { country: [] },
            fields: ["address_components", "formatted_address", "geometry", "name"]
        });

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            console.log("Service area place selected:", place);
            if (place.geometry) {
                setAutocompletePlace(place);
                setSearchQuery(place.formatted_address || "");
                setShowAutocompleteSuggestions(true);
            }
        });

        console.log("Service area autocomplete initialized successfully");
    };

    // Initialize autocomplete when ref is available
    useEffect(() => {
        if (searchInputRef.current && window.google && window.google.maps) {
            initServiceAreaAutocomplete();
        }
    }, [searchInputRef.current, window.google]);

    // Helper function to get service area type display name
    const getServiceAreaTypeDisplayName = (serviceArea: ServiceArea): string => {
        if (!serviceArea.type) return "Unknown Type";

        // If type is an object with name property
        if (typeof serviceArea.type === "object" && serviceArea.type.name) {
            return serviceArea.type.name;
        }

        // If type is an object with id property, map to display names
        if (typeof serviceArea.type === "object" && serviceArea.type.id) {
            const typeId = serviceArea.type.id;
            const typeNames: { [key: number]: string } = {
                1: "Metropolitan Area",
                2: "City",
                3: "Region",
                4: "Province"
            };
            return typeNames[typeId] || `Type ${typeId}`;
        }

        // If type is a direct number
        if (typeof serviceArea.type === "number") {
            const typeNames: { [key: number]: string } = {
                1: "Metropolitan Area",
                2: "City",
                3: "Region",
                4: "Province"
            };
            return typeNames[serviceArea.type] || `Type ${serviceArea.type}`;
        }

        // Fallback
        return "Unknown Type";
    };

    const fetchServiceAreas = async () => {
        setLoading(true);
        try {
            const response = await dispatch(getAllServiceAreas({
                page: 1,
                limit: 1000,
                isShowLoader: false
            })).unwrap();

            if (response && response.data) {
                console.log("Service areas fetched:", response.data);
                // Log the first service area to see its structure
                if (response.data.length > 0) {
                    console.log("First service area structure:", response.data[0]);
                    console.log("Type property:", response.data[0].type);
                }
                setAllServiceAreas(response.data);
            }
        } catch (error) {
            console.error("Error fetching service areas:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredServiceAreas = useMemo(() => {
        if (!searchQuery || searchQuery.length < 2) return [];

        const query = searchQuery.toLowerCase();
        return allServiceAreas.filter(area => {
            const nameMatches = area.name.toLowerCase().includes(query);
            const provinceMatches = area.province.toLowerCase().includes(query);
            const countryMatches = area.country?.name?.toLowerCase().includes(query) || false;
            const cityMatches = area.cities.some(city =>
                city.toLowerCase().includes(query)
            );

            return nameMatches || provinceMatches || countryMatches || cityMatches;
        }).filter(area =>
            !selectedServiceAreas.some(selected => selected.id === area.id)
        );
    }, [allServiceAreas, searchQuery, selectedServiceAreas]);

    // Function to clear autocomplete selection
    const clearAutocompleteSelection = () => {
        setAutocompletePlace(null);
        setSearchQuery("");
        setShowAutocompleteSuggestions(false);
    };

    // Function to create new service area from autocomplete selection
    const createServiceAreaFromPlace = async (place: any) => {
        if (!place || !place.geometry) {
            GlobalService.showToast("error", t("toast.titles.error"), t("serviceAreaSelection.toastInvalidLocation"));
            return;
        }

        setIsCreatingServiceArea(true);
        try {
            // Extract location data using utility function
            const locationData: LocationData = extractLocationFromPlace(place);

            // Find or create country
            let countryId = "";
            if (locationData.country) {
                try {
                    countryId = await findOrCreateCountry(
                        locationData.country,
                        countries || [],
                        createCountry,
                        dispatch
                    );
                } catch (error) {
                    console.error("Error handling country:", error);
                    GlobalService.showToast("error", t("toast.titles.error"), t("serviceAreaSelection.toastCountryError"));
                    setIsCreatingServiceArea(false);
                    return;
                }
            }

            // Prepare cities and postal codes arrays
            const cities = locationData.city ? [locationData.city] : [];
            let postalCodes = [];

            if (locationData.postalCode) {
                postalCodes = [locationData.postalCode];
            } else if (locationData.latitude && locationData.longitude && locationData.country) {
                // Generate a postal code as fallback
                const mockPostalCode = generateMockPostalCode(
                    locationData.latitude,
                    locationData.longitude,
                    locationData.country,
                    locationData.city
                );
                if (mockPostalCode) {
                    postalCodes = [mockPostalCode];
                }
            }

            // Create service area payload
            const serviceAreaPayload = {
                name: place.formatted_address || locationData.city || "New Service Area",
                type: { id: 2 }, // City type
                radius: 50, // Default radius
                restrictions: [],
                cities: cities,
                postalCodes: postalCodes,
                country: { id: countryId },
                serviceTypes: [t("generalServices")],
                emergencyServices: false,
                province: locationData.province || "",
                latitude: locationData.latitude,
                longitude: locationData.longitude
            };

            // Create the service area
            const createResult = await dispatch(createServiceArea({ data: serviceAreaPayload }));

            if (createResult.meta.requestStatus === "fulfilled") {
                const newServiceArea = createResult.payload;

                // Add the new service area to selected areas
                onServiceAreasChange([...selectedServiceAreas, newServiceArea]);

                // Clear search and show success
                setSearchQuery("");
                setShowAutocompleteSuggestions(false);
                setAutocompletePlace(null);

                GlobalService.showToast("success", t("toast.titles.success"), t("serviceAreaSelection.toastCreatedSuccess"));

                // Refresh service areas list
                fetchServiceAreas();
            } else {
                throw new Error("Failed to create service area");
            }
        } catch (error) {
            console.error("Error creating service area:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("serviceAreaSelection.toastCreationFailed"));
        } finally {
            setIsCreatingServiceArea(false);
        }
    };

    const handleServiceAreaSelect = (serviceArea: ServiceArea) => {
        if (!selectedServiceAreas.some(selected => selected.id === serviceArea.id)) {
            onServiceAreasChange([...selectedServiceAreas, serviceArea]);
        }
        setSearchQuery("");
        setShowDropdown(false);
        setShowAutocompleteSuggestions(false);
    };

    const handleServiceAreaRemove = (serviceAreaId: string) => {
        const updatedAreas = selectedServiceAreas.filter(area => area.id !== serviceAreaId);
        onServiceAreasChange(updatedAreas);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        setShowDropdown(value.length >= 2);

        // Clear autocomplete suggestions when user types
        if (value !== autocompletePlace?.formatted_address) {
            setShowAutocompleteSuggestions(false);
            setAutocompletePlace(null);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {professionalType && professionalType === RoleEnum.professional ? t("serviceAreaSelection.serviceAreas") : t("serviceAreaSelection.myWorkplace")}
                </label>

                <div className="relative">
                    <div className="relative">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t("serviceAreaSelection.searchPlaceholder")}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            disabled={disabled}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        {searchQuery && (
                            <button
                                onClick={() => {
                                    setSearchQuery("");
                                    setShowDropdown(false);
                                    setShowAutocompleteSuggestions(false);
                                    setAutocompletePlace(null);
                                }}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                disabled={disabled}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>

                    {/* Google Maps API Status */}
                    {!isGoogleMapsAvailable && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-xs text-yellow-800">
                                <strong>Note:</strong> {t("serviceAreaSelection.googleMapsNote")}
                            </p>
                        </div>
                    )}

                    {searchQuery.length > 0 && searchQuery.length < 2 && (
                        <p className="text-sm text-amber-600 mt-1">
                            {t("serviceAreaSelection.minCharactersWarning")}
                        </p>
                    )}
                    {searchQuery.length >= 2 && (
                        <p className="text-sm text-gray-600 mt-1">
                            {t("serviceAreaSelection.foundResults", { count: filteredServiceAreas.length, query: searchQuery, context: filteredServiceAreas.length === 1 ? "one" : "other" })}
                        </p>
                    )}

                    {/* Dropdown with existing service areas and autocomplete suggestions */}
                    {(showDropdown || showAutocompleteSuggestions) && searchQuery.length >= 2 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center text-gray-500">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                    <p className="mt-2">{t("serviceAreaSelection.loadingServiceAreas")}</p>
                                </div>
                            ) : (
                                <div className="py-2">
                                    {/* Show existing service areas */}
                                    {filteredServiceAreas.length > 0 && (
                                        <div className="border-b border-gray-200 pb-2 mb-2">
                                            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                {professionalType && professionalType === RoleEnum.professional ? t("serviceAreaSelection.existingServiceAreas") : t("serviceAreaSelection.existingWorkplaces")}
                                            </div>
                                            {filteredServiceAreas.map((area) => (
                                                <button
                                                    key={area.id}
                                                    onClick={() => handleServiceAreaSelect(area)}
                                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-gray-900 truncate">
                                                                {area.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500 truncate">
                                                                {area.province}{area.country?.name ? `, ${area.country.name}` : ""}
                                                            </div>
                                                        </div>
                                                        <Badge variant="outline" className="text-xs">
                                                            {getServiceAreaTypeDisplayName(area)}
                                                        </Badge>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Show autocomplete suggestions for creating new service areas */}
                                    {filteredServiceAreas.length === 0 && showAutocompleteSuggestions && autocompletePlace && (
                                        <div className="border-t border-gray-200 pt-2">
                                            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                {t("serviceAreaSelection.createNewServiceArea")}
                                            </div>
                                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mx-2 mb-2">
                                                <div className="flex flex-row gap-2">
                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium text-green-800">
                                                            {t("serviceAreaSelection.selectedLocation")}
                                                        </p>
                                                        <p className="text-xs text-green-700 break-words">
                                                            {autocompletePlace.formatted_address}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={clearAutocompleteSelection}
                                                            className="p-1 text-gray-400 hover:text-gray-600"
                                                            title={t("serviceAreaSelection.clearSelection")}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                        <Button
                                                            onClick={() => createServiceAreaFromPlace(autocompletePlace)}
                                                            disabled={isCreatingServiceArea || disabled}
                                                            className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded"
                                                        >
                                                            {isCreatingServiceArea ? (
                                                                <div className="flex items-center gap-1">
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                    <span>{t("serviceAreaSelection.creating")}</span>
                                                                </div>
                                                            ) : (
                                                                t("serviceAreaSelection.createServiceAreaButton")
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Show message when no results found */}
                                    {filteredServiceAreas.length === 0 && !showAutocompleteSuggestions && (
                                        <div className="p-4 text-center text-gray-500">
                                            <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                            <p>{t("serviceAreaSelection.noResultsFound", { query: searchQuery })}</p>
                                            {isGoogleMapsAvailable && (
                                                <p className="text-sm mt-1">
                                                    {t("serviceAreaSelection.tryAutocomplete")}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {selectedServiceAreas.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-800 mb-3">{professionalType && professionalType === RoleEnum.professional ? t("serviceAreaSelection.selectedServiceAreas") : t("serviceAreaSelection.selectedWorkplaces")}</h3>
                    <div className="flex flex-wrap gap-2">
                        {selectedServiceAreas.map((area) => (
                            <Badge
                                key={area.id}
                                variant="outline"
                                className="text-xs bg-blue-100 border-blue-300 text-blue-800 flex items-center gap-1"
                            >
                                <MapPin className="w-3 h-3" />
                                {area.name}
                                {!disabled && (
                                    <button
                                        onClick={() => handleServiceAreaRemove(area.id)}
                                        className="ml-1 hover:text-blue-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceAreaSelection;
